import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ArrowLeft, ShieldAlert } from "lucide-react";
import { AnnouncementText } from "@/components/AnnouncementBanner";
import { AppIcon } from "@/components/AppIcon";
import { Badge } from "@/components/ui/Badge";
import { AccessMatrix } from "@/pages/AccessMatrix";
import { DigestAdmin } from "@/pages/DigestAdmin";
import { FeedbackAdmin } from "@/pages/FeedbackAdmin";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import {
  ANNOUNCEMENT_LIMITS,
  useAdminApps,
  useAdminUsers,
  useAllAnnouncements,
  useAnnouncementMutations,
  useAppVisibility,
  useSetRequestable,
  useAuditLog,
  useCreateUser,
  useSetRole,
  useSetSuspended,
  type AdminUser,
} from "@/lib/admin";
import { cn } from "@/lib/cn";
import type { Me, Role } from "@/lib/me";

const ROLES: Role[] = ["employee", "admin", "superadmin"];

function relative(iso: string | null): string {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p className="text-destructive mt-2 text-sm">
      {error instanceof Error ? error.message : "Something went wrong."}
    </p>
  );
}

function CreateUser() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("employee");
  const create = useCreateUser();

  return (
    <form
      className="border-border bg-card mb-6 rounded-lg border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate(
          { email, name: name || undefined, role },
          { onSuccess: () => { setEmail(""); setName(""); setRole("employee"); } },
        );
      }}
    >
      <h3 className="mb-1 font-medium">Add someone</h3>
      <p className="text-muted-foreground mb-3 text-sm">
        Creates their sign-in account straight away — they can sign in whenever they
        like. Only @leychoon.com addresses.
      </p>
      {/* Stacked on a phone: side by side, `flex-1` squeezed the email field
          to about six characters — narrow enough that you could not read the
          address you had just typed. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@leychoon.com"
          className="h-9 sm:max-w-64 sm:flex-1"
        />
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Full name (optional)"
          className="h-9 sm:max-w-48 sm:flex-1"
        />
        <div className="flex gap-2">
          <Select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="flex-1 sm:flex-none"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
          <Button size="sm" type="submit" disabled={create.isPending}>
            {create.isPending ? "Adding…" : "Add"}
          </Button>
        </div>
      </div>
      <ErrorNote error={create.error} />
    </form>
  );
}

/**
 * What each role can actually do.
 *
 * ⚠ This describes the API's rules (`api/src/auth/guards.ts`), not the UI's.
 * If a guard changes, this copy has to change with it — a role table that
 * quietly drifts from the gate is worse than none.
 */
const ROLE_GUIDE: { role: Role; summary: string; limits: string }[] = [
  {
    role: "employee",
    summary:
      "Sees the app directory, opens apps, stars favourites and sends feedback.",
    limits: "No access to this Administration page at all.",
  },
  {
    role: "admin",
    summary:
      "Everything an employee can, plus: add people, change roles between employee and admin, suspend and restore access, post announcements, answer feedback, and read the audit log.",
    limits:
      "Cannot manage a superadmin, cannot change their own role, and cannot suspend themselves.",
  },
  {
    role: "superadmin",
    summary:
      "Everything an admin can, plus granting the superadmin role and viewing the cross-app access matrix.",
    limits:
      "There is exactly one, and the last superadmin can never be demoted or suspended — that is what stops the estate being locked out of itself.",
  },
];

function RoleGuide() {
  return (
    <section className="border-border bg-card mb-6 rounded-lg border p-4">
      <h3 className="mb-1 font-medium">What each role can do</h3>
      <p className="text-muted-foreground mb-3 text-sm">
        Roles apply to this directory. Each app decides its own access
        separately — being an admin here does not grant admin anywhere else.
      </p>

      <dl className="space-y-3">
        {ROLE_GUIDE.map((r) => (
          <div key={r.role} className="flex flex-col gap-1 sm:flex-row sm:gap-3">
            <dt className="sm:w-28 sm:shrink-0">
              <Badge variant={r.role === "employee" ? "neutral" : "success"}>
                {r.role}
              </Badge>
            </dt>
            <dd className="text-sm">
              {r.summary}{" "}
              <span className="text-muted-foreground">{r.limits}</span>
            </dd>
          </div>
        ))}
      </dl>

      <p className="text-muted-foreground mt-3 text-xs">
        Suspending someone locks their sign-in account, which stops them
        reaching every leychoon.app app — not just this one. All of these limits
        are enforced by the server, so they hold even if a button is visible.
      </p>
    </section>
  );
}

function UserRow({ user, me }: { user: AdminUser; me: Me }) {
  const setRole = useSetRole();
  const setSuspended = useSetSuspended();
  const isSelf = user.id === me.userId;

  return (
    <tr className="border-border border-t">
      <td className="py-2.5 pr-3">
        <div className="font-medium">{user.name ?? "—"}</div>
        <div className="text-muted-foreground text-xs">{user.email}</div>
      </td>
      <td className="py-2.5 pr-3">
        <Select
          value={user.role ?? "employee"}
          disabled={isSelf || setRole.isPending}
          onChange={(e) => setRole.mutate({ id: user.id, role: e.target.value as Role })}
          className="h-8"
          aria-label={`Role for ${user.email}`}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
      </td>
      <td className="text-muted-foreground py-2.5 pr-3 text-sm">
        {user.hasSignedIn ? relative(user.lastSeenAt) : "not yet signed in"}
      </td>
      <td className="py-2.5 pr-3">
        {user.deactivated ? (
          <Badge variant="destructive">Suspended</Badge>
        ) : (
          <Badge variant="success">Active</Badge>
        )}
      </td>
      <td className="py-2.5 text-right">
        <Button
          size="sm"
          variant={user.deactivated ? "outline" : "ghost"}
          disabled={isSelf || setSuspended.isPending}
          title={isSelf ? "You cannot suspend yourself" : undefined}
          onClick={() =>
            setSuspended.mutate({ id: user.id, suspended: !user.deactivated })
          }
          className={cn(!user.deactivated && "text-destructive")}
        >
          {user.deactivated ? "Restore" : "Suspend"}
        </Button>
      </td>
    </tr>
  );
}

function Users({ me }: { me: Me }) {
  const { data, isPending } = useAdminUsers();

  if (isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;

  return (
    <>
      <RoleGuide />
      <CreateUser />

      {data && !data.clerkLive && (
        <p className="border-warning/30 bg-warning/10 text-warning mb-4 flex items-start gap-2 rounded-lg border px-4 py-3 text-sm">
          <ShieldAlert size={16} className="mt-0.5 shrink-0" />
          No Clerk secret key is configured, so sign-in accounts are being simulated.
          Creating and suspending people here will not affect the other apps.
        </p>
      )}

      <div className="border-border bg-card overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[38rem] text-left text-sm">
          <thead className="text-muted-foreground text-xs uppercase">
            <tr>
              <th className="px-4 py-2.5 font-medium">Person</th>
              <th className="py-2.5 pr-3 font-medium">Role</th>
              <th className="py-2.5 pr-3 font-medium">Last seen</th>
              <th className="py-2.5 pr-3 font-medium">Status</th>
              <th />
            </tr>
          </thead>
          <tbody className="[&>tr>td:first-child]:px-4">
            {data?.users.map((u) => (
              <UserRow key={u.id} user={u} me={me} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-muted-foreground mt-3 text-xs">
        Suspending someone locks them out of every leychoon.app app, not just this
        one.
      </p>
    </>
  );
}

/**
 * Apps deliberately kept off the directory grid: platform utilities that staff
 * have no reason to launch. Listing them here rather than on the landing page
 * keeps the front door to things people actually open.
 */
function AdminApps() {
  const { data, isPending } = useAdminApps();

  if (isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;

  return (
    <>
      <p className="text-muted-foreground mb-4 text-sm">
        Hidden from the directory for everyone, including you — they live here
        instead. Opening one still goes through that app's own sign-in; this
        list grants nothing.
      </p>

      {data?.length === 0 ? (
        <p className="text-muted-foreground text-sm">No hidden apps.</p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {data?.map((app) => (
            <li
              key={app.id}
              className="group border-border bg-card relative flex gap-3 rounded-lg border p-4"
            >
              <AppIcon title={app.title} iconPath={app.iconPath} />
              <div className="min-w-0 flex-1">
                <a
                  href={app.url}
                  className="text-card-foreground group-hover:text-primary font-medium transition-colors after:absolute after:inset-0 after:rounded-lg"
                >
                  {app.title}
                </a>
                {app.description ? (
                  <p className="text-muted-foreground mt-1 text-sm">
                    {app.description}
                  </p>
                ) : (
                  <p className="text-muted-foreground/70 mt-1 text-sm italic">
                    No description yet
                  </p>
                )}
                <p className="text-muted-foreground mt-2 text-xs">{app.category}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * Which apps are listed, with a "Request access" form, to people who cannot use them.
 *
 * ⚠ Superadmin only, and the API enforces it. Switching an app on shows it to
 * everybody the app says it does not know, which for most apps is most of the company.
 */
function AppVisibilityAdmin() {
  const { data, isPending, error } = useAppVisibility();
  const setRequestable = useSetRequestable();

  if (isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;
  if (error) return <ErrorNote error={error} />;

  return (
    <>
      <p className="text-muted-foreground mb-4 text-sm">
        Switch an app on to list it under “Apps you can request” for people who
        don’t have access, with a form that emails the admin team. Every app starts
        off, including new ones. Home grants nothing: the request is a message, and
        access is still given in the app itself.
      </p>
      <ErrorNote error={setRequestable.error} />

      <ul className="border-border bg-card divide-border divide-y rounded-lg border">
        {data?.map((app) => {
          const inputId = `requestable-${app.id}`;
          return (
            <li key={app.id} className="flex items-center gap-3 px-4 py-3">
              <AppIcon title={app.title} iconPath={app.iconPath} size={32} />
              <div className="min-w-0 flex-1">
                <label htmlFor={inputId} className="font-medium">
                  {app.title}
                </label>
                <p className="text-muted-foreground text-xs">{app.category}</p>
              </div>
              {app.openToAll ? (
                <Badge variant="success">Open to everyone</Badge>
              ) : (
                <input
                  id={inputId}
                  type="checkbox"
                  role="switch"
                  aria-checked={app.requestable}
                  checked={app.requestable}
                  disabled={setRequestable.isPending}
                  onChange={(e) =>
                    setRequestable.mutate({ id: app.id, requestable: e.target.checked })
                  }
                  className="accent-primary h-5 w-5 shrink-0 cursor-pointer"
                />
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

function Announcements() {
  const { data } = useAllAnnouncements();
  const { create, remove } = useAnnouncementMutations();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [level, setLevel] = useState<"info" | "warning">("info");

  return (
    <>
      <form
        className="border-border bg-card mb-6 rounded-lg border p-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(
            { title, body, level },
            {
              onSuccess: () => {
                setTitle("");
                setBody("");
              },
            },
          );
        }}
      >
        <h3 className="mb-1 font-medium">Post an announcement</h3>
        <p className="text-muted-foreground mb-3 text-sm">
          Shown to everyone on the directory. Warnings cannot be dismissed. Line breaks
          are kept, and addresses like namecard.leychoon.app become links.
        </p>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium" htmlFor="announcement-title">
            Title <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <Input
            id="announcement-title"
            maxLength={ANNOUNCEMENT_LIMITS.title}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Your digital namecard is here"
          />
          <label className="mt-1 text-sm font-medium" htmlFor="announcement-body">
            Message
          </label>
          <textarea
            id="announcement-body"
            required
            rows={4}
            maxLength={ANNOUNCEMENT_LIMITS.body}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Worker Profile is down for maintenance until 3pm."
            className="border-input bg-card text-foreground placeholder:text-muted-foreground w-full rounded-md border px-3 py-2 text-sm leading-relaxed"
          />
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Select
              aria-label="Level"
              value={level}
              onChange={(e) => setLevel(e.target.value as "info" | "warning")}
            >
              <option value="info">Info</option>
              <option value="warning">Warning</option>
            </Select>
            <span className="text-muted-foreground flex-1 text-xs tabular-nums">
              {body.length}/{ANNOUNCEMENT_LIMITS.body}
            </span>
            <Button size="sm" type="submit" disabled={create.isPending}>
              Post
            </Button>
          </div>
        </div>
        <ErrorNote error={create.error} />
      </form>

      <ul className="space-y-2">
        {data?.length === 0 && (
          <li className="text-muted-foreground text-sm">Nothing posted yet.</li>
        )}
        {data?.map((a) => (
          <li
            key={a.id}
            className="border-border bg-card flex items-start gap-3 rounded-lg border p-3 text-sm"
          >
            <Badge variant={a.level === "warning" ? "warning" : "neutral"}>
              {a.level}
            </Badge>
            <div className="min-w-0 flex-1">
              {a.title && <p className="font-semibold">{a.title}</p>}
              <AnnouncementText text={a.body} className={a.title ? "mt-0.5" : undefined} />
              <p className="text-muted-foreground mt-1 text-xs">
                {new Date(a.startsAt).toLocaleString()} · {a.authorEmail ?? "unknown"}
              </p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => remove.mutate(a.id)}>
              Remove
            </Button>
          </li>
        ))}
      </ul>
    </>
  );
}

function Audit() {
  const { data, isPending } = useAuditLog();
  if (isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;

  return (
    <div className="border-border bg-card overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[34rem] text-left text-sm">
        <thead className="text-muted-foreground text-xs uppercase">
          <tr>
            <th className="px-4 py-2.5 font-medium">When</th>
            <th className="py-2.5 pr-3 font-medium">Action</th>
            <th className="py-2.5 pr-3 font-medium">By</th>
            <th className="py-2.5 pr-4 font-medium">Detail</th>
          </tr>
        </thead>
        <tbody>
          {data?.length === 0 && (
            <tr className="border-border border-t">
              <td colSpan={4} className="text-muted-foreground px-4 py-4">
                Nothing recorded yet.
              </td>
            </tr>
          )}
          {data?.map((e) => (
            <tr key={e.id} className="border-border border-t">
              <td className="text-muted-foreground px-4 py-2.5 whitespace-nowrap">
                {new Date(e.at).toLocaleString()}
              </td>
              <td className="py-2.5 pr-3 font-medium">{e.action}</td>
              <td className="text-muted-foreground py-2.5 pr-3">
                {e.actorEmail ?? "—"}
              </td>
              <td className="text-muted-foreground py-2.5 pr-4 text-xs">
                {e.detail ? JSON.stringify(e.detail) : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const BASE_TABS = [
  "People",
  "Feedback",
  "Announcements",
  "Admin apps",
  "Audit",
] as const;
/** ⚠ The matrix is every app's full roster in one view, app visibility advertises
    an app to the whole company, and the digest is the estate's whole day —
    superadmin only, and the API enforces that regardless of which tab the UI offers. */
const SUPERADMIN_TABS = ["Access matrix", "App visibility", "Digest"] as const;
type Tab = (typeof BASE_TABS)[number] | (typeof SUPERADMIN_TABS)[number];

export function Admin({ me }: { me: Me }) {
  const tabs: Tab[] = me.isSuperadmin
    ? [...BASE_TABS, ...SUPERADMIN_TABS]
    : [...BASE_TABS];
  // `?tab=feedback` opens straight on a tab — the feedback alert email links
  // here, and the daily digest's footer to `?tab=digest`. Only tabs this person
  // is offered can match.
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(
    () =>
      tabs.find((t) => t.toLowerCase() === params.get("tab")?.toLowerCase()) ??
      "People",
  );

  return (
    <main className="mx-auto max-w-5xl px-5 py-7">
      <Link
        to="/"
        className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1.5 text-sm"
      >
        <ArrowLeft size={14} /> Back to apps
      </Link>

      <h1 className="text-xl font-semibold tracking-tight">Administration</h1>
      <p className="text-muted-foreground mt-1 mb-6 text-sm">
        Who can sign in to the leychoon.app apps, and what has been changed.
      </p>

      {/*
        ⚠ The tab strip scrolls sideways rather than wrapping or stretching.
        Six tabs are wider than a phone, and without `overflow-x-auto` the row
        widened the whole page — every section below then sat in a document
        wider than the screen. The negative margin lets it bleed to the screen
        edge so the last tab reads as scrollable rather than clipped.
        ⚠ The grey line belongs to the inner row, not the scroller. Each tab's
        underline hangs 1px below its button (`-mb-px`) to sit on that line; with
        the line on the scroller, that 1px overflowed it, and since a box that
        scrolls one way scrolls both, Windows drew a vertical scrollbar beside
        the tabs. On the inner row the overlap stays inside its own border, and
        `overflow-y-hidden` keeps any rounding from bringing the bar back.
      */}
      <div className="-mx-5 mb-6 overflow-x-auto overflow-y-hidden px-5">
        <div className="border-border flex w-max min-w-full gap-1 border-b">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                "-mb-px border-b-2 px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors",
                tab === t
                  ? "border-primary text-foreground"
                  : "text-muted-foreground hover:text-foreground border-transparent",
              )}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {tab === "People" && <Users me={me} />}
      {tab === "Admin apps" && <AdminApps />}
      {tab === "Feedback" && <FeedbackAdmin />}
      {tab === "Access matrix" && <AccessMatrix />}
      {tab === "App visibility" && <AppVisibilityAdmin />}
      {tab === "Announcements" && <Announcements />}
      {tab === "Audit" && <Audit />}
      {tab === "Digest" && <DigestAdmin />}
    </main>
  );
}
